import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { accountSectionScrollDelta } from './accountSectionScroll'
import { loadSavedAccountCredentials } from './credentialSave'
import { signupPageTitle } from './pageTitle'
import { scrollStepIntoView } from './stepScroll'
import {
  clearOAuthHandoffQuery,
  clearPurchaseStatus,
  checkEmailEligibility,
  finalizeAccount,
  getLanguageQuery,
  getSignupId,
  getStepPath,
  getStepFromPath,
  loadConfiguration,
  loadOAuthHandoff,
  loadPurchaseStatus,
  resendWelcomeEmail,
  setLanguageQuery,
  setStepPath,
  submitPurchase,
  submitProUpsell,
  updateAccountCredentials,
  updatePurchaseLanguage,
} from './api'
import {
  BrandPanel,
  NavigationDock,
  ProgressHeader,
} from './components'
import {
  getLoadedMessages,
  I18nProvider,
  loadLanguageMessages,
  translate,
} from './i18n'
import {
  AccountStep,
  BundleStep,
  DoneStep,
  PurchaseStep,
  ProUpsellStep,
} from './steps'
import type {
  AccountResponse,
  ExistingAccountLinks,
  OAuthHandoff,
  PurchaseResponse,
  ProUpsellResponse,
  SignupConfiguration,
  SignupFormState,
  SignupStep,
} from './types'

const signupId = getSignupId()

function createPurchaseAttemptId(): string {
  if (typeof crypto.randomUUID === 'function')
    return crypto.randomUUID()

  const bytes = crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0'))

  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10).join(''),
  ].join('-')
}

const initialForm: SignupFormState = {
  packageId: null,
  emailAddress: '',
  password: '',
  confirmPassword: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  cardName: '',
  cardNumber: '',
  cardExpiry: '',
  cardCode: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  countryId: null,
  stateProvinceId: null,
  postalZip: '',
  termsAccepted: false,
  accountFields: {},
  marketingSourceId: null,
  addonSelected: false,
}

const legacyPrefillParameters: Record<string, string> = {
  businessNumber: 'myID',
  phoneNumber: 'myPhone',
  emailAddress: 'myEmail',
  firstName: 'myFName',
  lastName: 'myLName',
  addressLine1: 'myAdr1',
  addressLine2: 'myAdr2',
  city: 'myCity',
  postalZip: 'myZip',
}

function createInitialForm(): SignupFormState {
  const parameters = new URLSearchParams(window.location.search)
  const prefilledAccountFields: Record<string, string> = {}
  const prefilledForm = { ...initialForm }

  Object.entries(legacyPrefillParameters).forEach(([key, parameter]) => {
    const value = parameters.get(parameter)
    if (!value)
      return

    if (key === 'businessNumber') {
      prefilledAccountFields.businessNumber = value
      prefilledAccountFields.diyBusinessInfo1Value = value
    }
    else
      Object.assign(prefilledForm, { [key]: value })
  })

  prefilledForm.accountFields = prefilledAccountFields
  return prefilledForm
}

function isEmailValid(emailAddress: string): boolean {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(emailAddress)
}

function formatCurrency(
  priceCents: number,
  currency: string,
  languageCode: string,
): string {
  return new Intl.NumberFormat(languageCode.replace('_', '-'), {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: priceCents % 100 === 0 ? 0 : 2,
  }).format(priceCents / 100)
}

function getScrollBehavior(): ScrollBehavior {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ? 'auto'
    : 'smooth'
}

function App() {
  const [configuration, setConfiguration] =
    useState<SignupConfiguration | null>(null)
  const [loadingError, setLoadingError] = useState('')
  const [languageCode, setLanguageCode] = useState(getLanguageQuery)
  const [messages, setMessages] = useState(() =>
    getLoadedMessages(getLanguageQuery()),
  )
  const [currentStep, setCurrentStep] = useState<SignupStep>(getStepFromPath)
  const [form, setForm] = useState<SignupFormState>(createInitialForm)
  const [credentialsEditing, setCredentialsEditing] = useState(false)
  const [purchaseAttemptId, setPurchaseAttemptId] = useState(
    createPurchaseAttemptId,
  )
  const [purchaseResponse, setPurchaseResponse] =
    useState<PurchaseResponse | null>(null)
  const [purchaseFlowId, setPurchaseFlowId] = useState<string | null>(null)
  const [credentialsEstablished, setCredentialsEstablished] = useState(false)
  const [purchaseStatusLoaded, setPurchaseStatusLoaded] = useState(false)
  const [purchaseError, setPurchaseError] = useState<PurchaseResponse | null>(null)
  const [purchaseSubmitting, setPurchaseSubmitting] = useState(false)
  const [purchaseRetryBlocked, setPurchaseRetryBlocked] = useState(false)
  const [emailEligibilityError, setEmailEligibilityError] = useState('')
  const [existingAccount, setExistingAccount] = useState<ExistingAccountLinks | null>(null)
  const [emailEligibilityChecking, setEmailEligibilityChecking] = useState(false)
  const [onboardingComplete, setOnboardingComplete] = useState(false)
  const [accountSubmitting, setAccountSubmitting] = useState(false)
  const [accountError, setAccountError] = useState('')
  const [accountFieldErrors, setAccountFieldErrors] = useState<Record<string, string>>({})
  const [welcomeResendSeconds, setWelcomeResendSeconds] = useState(0)
  const [welcomeResendMessage, setWelcomeResendMessage] = useState('')
  const [welcomeResendSubmitting, setWelcomeResendSubmitting] = useState(false)
  const [proUpsellAttemptId, setProUpsellAttemptId] = useState(
    createPurchaseAttemptId,
  )
  const [proUpsellResponse, setProUpsellResponse] =
    useState<ProUpsellResponse | null>(null)
  const [proUpsellSubmitting, setProUpsellSubmitting] = useState(false)
  const [oauthHandoff, setOAuthHandoff] = useState<OAuthHandoff | null>(null)
  const previousPackageId = useRef(form.packageId)

  useEffect(() => {
    let active = true
    let requestController: AbortController | null = null

    const synchronizePurchaseStatus = () => {
      setPurchaseStatusLoaded(false)
      requestController?.abort()
      requestController = new AbortController()

      loadPurchaseStatus(signupId, requestController.signal)
        .then((status) => {
          if (!active)
            return

          if (status.completed && status.purchase) {
            setPurchaseResponse(status.purchase)
            setPurchaseFlowId(status.flowId)
            setCredentialsEstablished(true)
            setOnboardingComplete(status.onboardingComplete)
            setWelcomeResendSeconds(
              status.welcomeEmailResendAvailableInSeconds,
            )
            setForm((current) => ({
              ...current,
              packageId: status.packageId ?? current.packageId,
              emailAddress: status.emailAddress ?? current.emailAddress,
            }))

            if (status.languageCode
                && status.languageCode !== getLanguageQuery()) {
              setLanguageQuery(status.languageCode)
              setLanguageCode(status.languageCode)
            }

            const restoredStep = getStepFromPath()
            const historyFlowId = window.history.state?.purchaseFlowId
            const belongsToCurrentFlow = Boolean(
              status.flowId && historyFlowId === status.flowId,
            )
            const restoredStepAllowed = status.onboardingComplete
              ? restoredStep === 'done' || restoredStep === 'addons-pro'
              : restoredStep === 'account'
            if (!belongsToCurrentFlow || !restoredStepAllowed) {
              const restoredTarget = status.onboardingComplete
                ? 'done'
                : 'account'
              setCurrentStep(restoredTarget)
              window.history.replaceState(
                { step: restoredTarget, purchaseFlowId: status.flowId },
                '',
                getStepPath(signupId, restoredTarget),
              )
            }
          }
          else if (status.purchase && !status.purchase.retryAllowed) {
            setPurchaseError(status.purchase)
            setPurchaseRetryBlocked(true)
            setForm((current) => ({
              ...current,
              packageId: status.packageId ?? current.packageId,
              emailAddress: status.emailAddress ?? current.emailAddress,
            }))
            setCurrentStep('purchase')
            window.history.replaceState(
              { step: 'purchase', purchaseFlowId: null },
              '',
              getStepPath(signupId, 'purchase'),
            )
          }

          setPurchaseStatusLoaded(true)
        })
        .catch((error: unknown) => {
          if (!active || (error instanceof DOMException && error.name === 'AbortError'))
            return

          setLoadingError(
            error instanceof Error
              ? error.message
              : 'We could not confirm the current purchase status.',
          )
          setPurchaseStatusLoaded(true)
        })
    }

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted)
        synchronizePurchaseStatus()
    }

    synchronizePurchaseStatus()
    window.addEventListener('pageshow', handlePageShow)

    return () => {
      active = false
      requestController?.abort()
      window.removeEventListener('pageshow', handlePageShow)
    }
  }, [])

  useEffect(() => {
    let active = true

    document.documentElement.lang = languageCode.replace('_', '-')
    loadLanguageMessages(languageCode)
      .then((loadedMessages) => {
        if (active)
          setMessages(loadedMessages)
      })
      .catch(() => {
        if (active)
          setMessages(getLoadedMessages('en'))
      })

    return () => {
      active = false
    }
  }, [languageCode])

  useEffect(() => {
    const abortController = new AbortController()

    // the signup works without it; only the done step's link back to the OAuth application is lost
    loadOAuthHandoff(signupId, abortController.signal)
      .then(setOAuthHandoff)
      .catch(() => undefined)

    return () => abortController.abort()
  }, [])

  useEffect(() => {
    const abortController = new AbortController()
    setLoadingError('')

    loadConfiguration(signupId, languageCode, abortController.signal)
      .then((loadedConfiguration) => {
        setConfiguration(loadedConfiguration)
        const parameters = new URLSearchParams(window.location.search)
        const prefilledCountry = loadedConfiguration.countries.find(
          (country) => country.name === parameters.get('myCountry'),
        )
        setForm((current) => {
          const selectedCountryId = current.countryId
            ?? prefilledCountry?.id
            ?? loadedConfiguration.defaultCountryId
            ?? null
          const prefilledState = loadedConfiguration.stateProvinces.find(
            (state) => state.name === parameters.get('myState')
              && (!selectedCountryId || state.countryId === selectedCountryId),
          )

          return {
            ...current,
            packageId: loadedConfiguration.billingOptions.some(
              (option) => option.id === current.packageId,
            ) ? current.packageId : null,
            countryId: selectedCountryId,
            stateProvinceId:
              current.stateProvinceId ?? prefilledState?.id ?? null,
          }
        })
        document.title = signupPageTitle(loadedConfiguration.brand)
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError')
          return
        setLoadingError(
          error instanceof Error
            ? error.message
            : 'We could not load this signup.',
        )
      })

    return () => abortController.abort()
  }, [languageCode])

  const selectedPackage = configuration?.billingOptions.find(
    (option) => option.id === form.packageId,
  )
  const proUpsell = configuration?.proUpsell
  const addonsVisible = Boolean(
    selectedPackage
      && proUpsell
      && !(selectedPackage.unlimitedDiy && selectedPackage.lifetime)
      && selectedPackage.id !== proUpsell.packageId
      && selectedPackage.currency.toLowerCase() === proUpsell.currency.toLowerCase()
  )
  const totalSteps = addonsVisible ? 4 : 3

  useEffect(() => {
    const handlePopState = () => {
      const nextStep = getStepFromPath()
      const historyFlowId = window.history.state?.purchaseFlowId
      const belongsToCurrentFlow = Boolean(
        purchaseFlowId && historyFlowId === purchaseFlowId,
      )

      if (purchaseResponse) {
        const allowedStep = onboardingComplete
          ? nextStep === 'done' || (addonsVisible && nextStep === 'addons-pro')
          : nextStep === 'account'

        if (!belongsToCurrentFlow || !allowedStep) {
          const targetStep = onboardingComplete ? 'done' : 'account'
          setCurrentStep(targetStep)
          window.history.replaceState(
            { step: targetStep, purchaseFlowId },
            '',
            getStepPath(signupId, targetStep),
          )
          return
        }
      }

      setCurrentStep(nextStep)
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [addonsVisible, onboardingComplete, purchaseFlowId, purchaseResponse])

  useEffect(() => {
    if (configuration && currentStep === 'addons-pro' && !addonsVisible) {
      const targetStep = onboardingComplete ? 'done' : 'account'
      setCurrentStep(targetStep)
      window.history.replaceState(
        { step: targetStep, purchaseFlowId },
        '',
        getStepPath(signupId, targetStep),
      )
    }
  }, [
    addonsVisible,
    configuration,
    currentStep,
    onboardingComplete,
    purchaseFlowId,
  ])

  useEffect(() => {
    const requiresCompletedPurchase =
      currentStep === 'account' || currentStep === 'addons-pro' || currentStep === 'done'

    if (configuration && purchaseStatusLoaded
        && requiresCompletedPurchase && !purchaseResponse) {
      setCurrentStep('bundle')
      window.history.replaceState(
        { step: 'bundle', purchaseFlowId: null },
        '',
        getStepPath(signupId, 'bundle'),
      )
    }
  }, [configuration, currentStep, purchaseResponse, purchaseStatusLoaded])

  useEffect(() => {
    if (welcomeResendSeconds <= 0)
      return

    const timer = window.setInterval(() => {
      setWelcomeResendSeconds((seconds) => Math.max(0, seconds - 1))
    }, 1000)

    return () => window.clearInterval(timer)
  }, [welcomeResendSeconds])

  useEffect(() => {
    const priorPackageId = previousPackageId.current
    previousPackageId.current = form.packageId
    if (currentStep !== 'bundle'
        || form.packageId === null
        || form.packageId === priorPackageId)
      return

    const animationFrame = window.requestAnimationFrame(() => {
      const accountSection = document.getElementById('bundle-account-section')
      if (!accountSection)
        return

      const accountBounds = accountSection.getBoundingClientRect()
      const flowMain = accountSection.closest('.flow-main')
      const flowMainStyle = flowMain ? window.getComputedStyle(flowMain) : null
      const flowMainScrolls = Boolean(
        flowMainStyle && /^(auto|scroll)$/.test(flowMainStyle.overflowY),
      )
      const flowBounds = flowMainScrolls
        ? flowMain?.getBoundingClientRect()
        : null
      const visualTop = window.visualViewport?.offsetTop ?? 0
      const visualBottom = visualTop + (window.visualViewport?.height ?? window.innerHeight)
      const viewportTop = Math.max(visualTop, flowBounds?.top ?? visualTop)
      let viewportBottom = Math.min(visualBottom, flowBounds?.bottom ?? visualBottom)
      const dockBounds = document.querySelector('.navigation-dock')?.getBoundingClientRect()
      if (dockBounds && dockBounds.bottom > viewportTop && dockBounds.top < viewportBottom)
        viewportBottom = Math.max(viewportTop, dockBounds.top)

      const scrollDelta = accountSectionScrollDelta(accountBounds, {
        top: viewportTop + 12,
        bottom: viewportBottom - 12,
      })
      if (scrollDelta !== 0) {
        const scroller = flowMainScrolls && flowMain ? flowMain : window
        scroller.scrollBy({ top: scrollDelta, behavior: getScrollBehavior() })
      }
    })

    return () => window.cancelAnimationFrame(animationFrame)
  }, [currentStep, form.packageId])

  useEffect(() => {
    if (currentStep !== 'purchase' || !purchaseError)
      return

    const animationFrame = window.requestAnimationFrame(() => {
      const errorAlert = document.getElementById('purchase-error-alert')
      if (!(errorAlert instanceof HTMLElement))
        return

      errorAlert.scrollIntoView({
        behavior: getScrollBehavior(),
        block: 'start',
      })
      errorAlert.focus({ preventScroll: true })
    })

    return () => window.cancelAnimationFrame(animationFrame)
  }, [currentStep, purchaseError])

  const stepsShown = Boolean(configuration && purchaseStatusLoaded)
  const scrolledStep = useRef<SignupStep | null>(null)
  useLayoutEffect(() => {
    if (!stepsShown)
      return

    const priorStep = scrolledStep.current
    scrolledStep.current = currentStep
    if (priorStep === null || priorStep === currentStep)
      return

    return scrollStepIntoView(currentStep === 'purchase' || currentStep === 'account')
  }, [currentStep, stepsShown])

  useEffect(() => {
    const beforeAccount = currentStep === 'bundle' || currentStep === 'purchase'

    if (purchaseResponse && beforeAccount) {
      const targetStep = onboardingComplete ? 'done' : 'account'
      setCurrentStep(targetStep)
      window.history.replaceState(
        { step: targetStep, purchaseFlowId },
        '',
        getStepPath(signupId, targetStep),
      )
    }
  }, [currentStep, onboardingComplete, purchaseFlowId, purchaseResponse])

  const updateForm = useCallback(
    <K extends keyof SignupFormState>(
      key: K,
      value: SignupFormState[K],
    ) => {
      if (currentStep === 'purchase' && purchaseError?.retryAllowed)
        setPurchaseError(null)
      if (key === 'emailAddress') {
        setEmailEligibilityError('')
        setExistingAccount(null)
      }
      if (currentStep === 'account'
          && (key === 'accountFields' || key === 'marketingSourceId')) {
        setAccountError('')
        setAccountFieldErrors({})
      }

      setForm((current) => {
        let nextValue = value

        if (key === 'cardNumber' && typeof value === 'string') {
          nextValue = value
            .replace(/\D/g, '')
            .slice(0, 16)
            .replace(/(\d{4})(?=\d)/g, '$1 ') as SignupFormState[K]
        }
        if (key === 'cardExpiry' && typeof value === 'string') {
          const digits = value.replace(/\D/g, '').slice(0, 4)
          nextValue = (digits.length > 2
            ? `${digits.slice(0, 2)}/${digits.slice(2)}`
            : digits) as SignupFormState[K]
        }
        if (key === 'cardCode' && typeof value === 'string') {
          nextValue = value.replace(/\D/g, '').slice(0, 4) as SignupFormState[K]
        }

        return { ...current, [key]: nextValue }
      })
    },
    [currentStep, purchaseError],
  )

  const credentialsValid =
    isEmailValid(form.emailAddress) &&
    form.password.length >= 6 &&
    form.password === form.confirmPassword

  const canContinue = useMemo(() => {
    if (!configuration)
      return false

    if (currentStep === 'bundle') {
      return form.packageId !== null
        && credentialsValid
        && !emailEligibilityChecking
    }

    if (currentStep === 'purchase') {
      const cardDigits = form.cardNumber.replace(/\D/g, '')
      const availableStates = configuration.stateProvinces.filter(
        (stateProvince) => stateProvince.countryId === form.countryId,
      )

      return configuration.features.purchaseEnabled
        && !purchaseSubmitting
        && !purchaseRetryBlocked
        && Boolean(
        form.firstName.trim() &&
          form.lastName.trim() &&
          form.cardName.trim() &&
          cardDigits.length >= 15 &&
          /^\d{2}\/\d{2}$/.test(form.cardExpiry) &&
          form.cardCode.length >= 3 &&
          form.addressLine1.trim() &&
          form.city.trim() &&
          form.countryId &&
          (availableStates.length === 0 || form.stateProvinceId) &&
          form.postalZip.trim() &&
          form.termsAccepted,
        )
    }

    if (currentStep === 'account') {
      const requiredFieldsValid = configuration.accountFields
        .filter((field) => field.required)
        .every((field) => form.accountFields[field.key]?.trim())

      return !credentialsEditing
        && credentialsEstablished
        && !accountSubmitting
        && requiredFieldsValid
    }

    if (currentStep === 'addons-pro') {
      return !proUpsellSubmitting
        && !(proUpsellResponse && !proUpsellResponse.retryAllowed)
    }

    return true
  }, [
    configuration,
    accountSubmitting,
    credentialsEditing,
    credentialsEstablished,
    credentialsValid,
    currentStep,
    form,
    purchaseRetryBlocked,
    purchaseSubmitting,
    emailEligibilityChecking,
    proUpsellResponse,
    proUpsellSubmitting,
  ])

  const goToStep = useCallback((
    step: SignupStep,
    flowIdOverride?: string | null,
  ) => {
    const flowId = flowIdOverride === undefined
      ? purchaseFlowId
      : flowIdOverride
    setCurrentStep(step)
    setStepPath(signupId, step, flowId)
  }, [purchaseFlowId])

  const t = useCallback(
    (key: string, ...values: Array<string | number>) =>
      translate(messages, key, ...values),
    [messages],
  )

  const continueLabel =
    currentStep === 'bundle'
      ? t('signup.nav.continue')
      : currentStep === 'purchase'
        ? purchaseSubmitting
          ? t('signup.purchase.processing')
          : t('signup.purchase.confirm', selectedPackage
            ? formatCurrency(
              selectedPackage.priceCents,
              selectedPackage.currency,
              languageCode,
            )
            : '')
        : currentStep === 'account'
          ? accountSubmitting
            ? t('signup.purchase.processing')
            : addonsVisible
              ? t('signup.nav.continue')
              : t('signup.profile.finish')
          : currentStep === 'addons-pro'
            ? proUpsellSubmitting
              ? t('signup.purchase.processing')
              : form.addonSelected && selectedPackage && proUpsell
                ? `Upgrade for ${formatCurrency(
                    Math.max(0, proUpsell.priceCents - selectedPackage.priceCents),
                    proUpsell.currency,
                    languageCode,
                  )}`
                : t('signup.profile.finish')
          : t('signup.profile.finish')

  const handleSaveCredentials = async (
    emailAddress: string,
    password: string,
    confirmPassword: string,
  ): Promise<AccountResponse> => {
    if (!purchaseFlowId)
      throw new Error(t('signup.account.error.session'))

    const response = await updateAccountCredentials(
      signupId,
      purchaseFlowId,
      emailAddress,
      password,
      confirmPassword,
      t,
    )

    if (response.status === 'SUCCESS') {
      setForm((current) => ({
        ...current,
        emailAddress: response.emailAddress || emailAddress,
        password,
        confirmPassword,
      }))
      setCredentialsEstablished(true)
      setCredentialsEditing(false)
    }

    return response
  }

  const handleCancelCredentials = async () => {
    if (!purchaseFlowId)
      throw new Error(t('signup.account.error.session'))
    const response = await loadSavedAccountCredentials(signupId, purchaseFlowId, t)
    if (response.status !== 'SUCCESS')
      throw new Error(response.message)
    setForm(current => ({
      ...current,
      emailAddress: response.emailAddress,
      // Passwords cannot be read back; do not retain a possibly outdated value.
      password: '',
      confirmPassword: '',
    }))
    setCredentialsEstablished(true)
    setCredentialsEditing(false)
  }

  const handleContinue = async () => {
    if (!canContinue)
      return

    if (currentStep === 'bundle') {
      setEmailEligibilityChecking(true)
      setEmailEligibilityError('')
      setExistingAccount(null)
      try {
        const response = await checkEmailEligibility(
          signupId,
          languageCode,
          form.emailAddress,
        )
        if (response.status === 'AVAILABLE')
          goToStep('purchase')
        else {
          setEmailEligibilityError(response.message)
          setExistingAccount(response.existingAccount ?? null)
        }
      }
      catch (error) {
        setEmailEligibilityError(
          error instanceof Error
            ? error.message
            : 'We could not check this email address. Please try again.',
        )
      }
      finally {
        setEmailEligibilityChecking(false)
      }
    }
    else if (currentStep === 'purchase') {
      setPurchaseSubmitting(true)
      setPurchaseError(null)

      try {
        const response = await submitPurchase(
          signupId,
          languageCode,
          purchaseAttemptId,
          form,
        )

        if (response.status === 'SUCCESS') {
          setForm((current) => ({
            ...current,
            cardName: '',
            cardNumber: '',
            cardExpiry: '',
            cardCode: '',
          }))
          setPurchaseResponse(response)
          setPurchaseFlowId(purchaseAttemptId)
          setCredentialsEstablished(true)
          setOnboardingComplete(false)
          setAccountError('')
          setAccountFieldErrors({})
          setWelcomeResendSeconds(0)
          setWelcomeResendMessage('')
          setPurchaseRetryBlocked(false)
          goToStep('account', purchaseAttemptId)
        }
        else {
          setPurchaseError(response)
          setPurchaseRetryBlocked(!response.retryAllowed)
          if (response.retryAllowed)
            setPurchaseAttemptId(createPurchaseAttemptId())
        }
      }
      catch (error) {
        setPurchaseError({
          status: 'PAYMENT_STATUS_UNKNOWN',
          message: error instanceof Error
            ? error.message
            : 'We could not confirm the purchase result. Do not retry yet; please contact support.',
          fieldErrors: {},
          paymentReceived: false,
          retryAllowed: false,
          simulated: false,
          testPaymentProcessed: false,
          orderId: '',
        })
        setPurchaseRetryBlocked(true)
      }
      finally {
        setPurchaseSubmitting(false)
      }
    }
    else if (currentStep === 'account') {
      if (!purchaseFlowId)
        return

      setAccountSubmitting(true)
      setAccountError('')
      setAccountFieldErrors({})

      try {
        const response = await finalizeAccount(
          signupId,
          purchaseFlowId,
          languageCode,
          form,
        )
        if (response.status === 'SUCCESS') {
          setOnboardingComplete(true)
          setWelcomeResendSeconds(
            response.welcomeEmailResendAvailableInSeconds,
          )
          setForm((current) => ({
            ...current,
            emailAddress: response.emailAddress || current.emailAddress,
            password: '',
            confirmPassword: '',
          }))
          if (!response.simulated && !response.emailsQueued) {
            setWelcomeResendMessage(
              'Your account is ready, but the welcome email was not queued. Please use resend.',
            )
          }
          goToStep(addonsVisible ? 'addons-pro' : 'done')
        }
        else {
          setAccountError(response.message)
          setAccountFieldErrors(response.fieldErrors)
        }
      }
      catch (error) {
        setAccountError(
          error instanceof Error
            ? error.message
            : 'We could not finish account setup. Your payment will not be submitted again.',
        )
      }
      finally {
        setAccountSubmitting(false)
      }
    }
    else if (currentStep === 'addons-pro') {
      if (!form.addonSelected) {
        goToStep('done')
        return
      }
      if (!purchaseFlowId)
        return

      setProUpsellSubmitting(true)
      setProUpsellResponse(null)
      try {
        const response = await submitProUpsell(
          signupId,
          purchaseFlowId,
          proUpsellAttemptId,
        )
        setProUpsellResponse(response)
        if (response.status === 'SUCCESS')
          goToStep('done')
        else if (response.retryAllowed)
          setProUpsellAttemptId(createPurchaseAttemptId())
      }
      catch (error) {
        setProUpsellResponse({
          status: 'PAYMENT_STATUS_UNKNOWN',
          message: error instanceof Error
            ? error.message
            : 'We could not confirm the Pro upgrade. Please do not retry yet.',
          chargeAmountCents: 0,
          currency: '',
          orderId: '',
          paymentReceived: false,
          retryAllowed: false,
          simulated: false,
          testPaymentProcessed: false,
        })
      }
      finally {
        setProUpsellSubmitting(false)
      }
    }
  }

  const handleBack = () => {
    if (currentStep === 'purchase')
      goToStep('bundle')
    else if (currentStep === 'account')
      goToStep('purchase')
    else if (currentStep === 'addons-pro')
      goToStep('account')
  }

  const handleLanguageChange = (nextLanguageCode: string) => {
    setLanguageQuery(nextLanguageCode)
    setLanguageCode(nextLanguageCode)

    if (purchaseResponse) {
      void updatePurchaseLanguage(signupId, nextLanguageCode)
        .catch(() => {
          setLoadingError('We could not save the selected language.')
        })
    }
  }

  const handleRestart = async () => {
    try {
      await clearPurchaseStatus(signupId)
    }
    catch (error) {
      setLoadingError(
        error instanceof Error ? error.message : 'We could not restart this signup.',
      )
      return
    }

    setForm({
      ...initialForm,
      countryId: configuration?.defaultCountryId ?? null,
    })
    setCredentialsEditing(false)
    setCredentialsEstablished(false)
    setPurchaseAttemptId(createPurchaseAttemptId())
    setPurchaseResponse(null)
    setPurchaseFlowId(null)
    setPurchaseError(null)
    setPurchaseRetryBlocked(false)
    setEmailEligibilityError('')
    setExistingAccount(null)
    setEmailEligibilityChecking(false)
    setOnboardingComplete(false)
    setAccountSubmitting(false)
    setAccountError('')
    setAccountFieldErrors({})
    setWelcomeResendSeconds(0)
    setWelcomeResendMessage('')
    setWelcomeResendSubmitting(false)
    setProUpsellAttemptId(createPurchaseAttemptId())
    setProUpsellResponse(null)
    setProUpsellSubmitting(false)
    // the next purchase is for someone else; it must not resume the first account's OAuth authorization
    setOAuthHandoff(null)
    clearOAuthHandoffQuery()
    goToStep('bundle', null)
  }

  const handleWelcomeEmailResend = async () => {
    if (!purchaseFlowId || welcomeResendSubmitting || welcomeResendSeconds > 0)
      return

    setWelcomeResendSubmitting(true)
    setWelcomeResendMessage('')
    try {
      const response = await resendWelcomeEmail(signupId, purchaseFlowId)
      setWelcomeResendSeconds(response.resendAvailableInSeconds)
      setWelcomeResendMessage(response.message)
    }
    catch (error) {
      setWelcomeResendMessage(
        error instanceof Error
          ? error.message
          : 'We could not resend the welcome email. Please try again.',
      )
    }
    finally {
      setWelcomeResendSubmitting(false)
    }
  }

  if (loadingError) {
    return (
      <main className="load-state error-state">
        <span aria-hidden="true">!</span>
        <h1>Signup unavailable</h1>
        <p>{loadingError}</p>
        <button
          className="button button-primary"
          type="button"
          onClick={() => window.location.reload()}
        >
          Try again
        </button>
      </main>
    )
  }

  if (!configuration || !purchaseStatusLoaded) {
    return (
      <main className="load-state" aria-live="polite">
        <div className="loading-spinner" />
        <p>Loading signup…</p>
      </main>
    )
  }

  return (
    <I18nProvider languageCode={languageCode} messages={messages}>
      <div className="signup-shell">
        <BrandPanel
          brand={configuration.brand}
          termsUrl={configuration.links.termsUrl}
        />
        <form
          className={`flow-panel ${currentStep === 'done' ? 'done-flow' : ''}`}
          noValidate
          onKeyDown={(event) => {
            if (event.key !== 'Enter'
                || event.defaultPrevented
                || event.nativeEvent.isComposing
                || event.repeat)
              return

            const target = event.target
            if (!(target instanceof HTMLInputElement)
                && !(target instanceof HTMLSelectElement))
              return

            event.preventDefault()
            void handleContinue()
          }}
          onSubmit={(event) => {
            event.preventDefault()
            void handleContinue()
          }}
        >
          {currentStep !== 'done' && (
            <ProgressHeader
              currentStep={currentStep}
              addonsVisible={addonsVisible}
            />
          )}
          <main className="flow-main">
            <div className="stage">
              {currentStep === 'bundle' && (
                <BundleStep
                  configuration={configuration}
                  form={form}
                  update={updateForm}
                  totalSteps={totalSteps}
                  emailError={emailEligibilityError}
                  existingAccount={existingAccount}
                />
              )}
              {currentStep === 'purchase' && (
                <PurchaseStep
                  configuration={configuration}
                  form={form}
                  update={updateForm}
                  selectedPackage={selectedPackage}
                  totalSteps={totalSteps}
                  purchaseError={purchaseError}
                />
              )}
              {currentStep === 'account' && (
                <AccountStep
                  configuration={configuration}
                  form={form}
                  update={updateForm}
                  credentialsEditing={credentialsEditing}
                  onEditCredentials={() => setCredentialsEditing(true)}
                  onSaveCredentials={handleSaveCredentials}
                  onCancelCredentials={handleCancelCredentials}
                  submissionError={accountError}
                  fieldErrors={accountFieldErrors}
                  totalSteps={totalSteps}
                />
              )}
              {currentStep === 'addons-pro'
                  && addonsVisible
                  && selectedPackage
                  && proUpsell && (
                <ProUpsellStep
                  configuration={configuration}
                  form={form}
                  update={updateForm}
                  totalSteps={totalSteps}
                  selectedPackage={selectedPackage}
                  proUpsell={proUpsell}
                  submissionError={proUpsellResponse}
                />
              )}
              {currentStep === 'done' && (
                <DoneStep
                  configuration={configuration}
                  form={form}
                  selectedPackage={selectedPackage}
                  purchaseResponse={purchaseResponse}
                  proUpsell={proUpsell}
                  proUpsellResponse={proUpsellResponse}
                  oauthHandoff={oauthHandoff}
                  onRestart={handleRestart}
                  resendAvailableInSeconds={welcomeResendSeconds}
                  resendMessage={welcomeResendMessage}
                  resendSubmitting={welcomeResendSubmitting}
                  onResend={() => void handleWelcomeEmailResend()}
                />
              )}
            </div>
          </main>
          <NavigationDock
            currentStep={currentStep}
            canContinue={canContinue}
            languages={configuration.languages}
            languageCode={languageCode}
            continueLabel={continueLabel}
            onBack={handleBack}
            onLanguageChange={handleLanguageChange}
            allowBack={currentStep !== 'account' || !purchaseResponse}
          />
        </form>
      </div>
    </I18nProvider>
  )
}

export default App
